import './styles.scss';

import { ReactElement, useEffect } from 'react';
import { customIndexedDBStorage } from '@/state/storage';
import { getBulkValues, handleError } from '@/utils';
import { handleCreateVehicleBulk, handleUpdateVehicleBulk } from '@/api/services/vehicles';

import EditVehiclesModal from '@/components/EditVehiclesModal';
import { LoadingBlur } from '@/components/LoadingSpinner';
import PageInfo from '@/components/PageInfo';
import { Vehicle } from '@/models/gen/graphql';
import VehicleFilters from '@/features/Vehicles/components/VehicleFilters';
import VehicleTable from '@/features/Vehicles/components/VehicleTable';
import { VehiclesTypes } from '@/features/Vehicles/types';
import { createPersistentComponentState } from '@/state';

export const useVehiclesState = createPersistentComponentState<VehiclesTypes.State>('VehiclesState', VehiclesTypes.initState, {
  storage: customIndexedDBStorage,
});

const Vehicles = (): ReactElement => {
  const [{ vehicles, total, loading, selected, editVehicle, createVehicle, fetch }, setState] = useVehiclesState(({ state, setState }) => [
    {
      vehicles: state.vehicles,
      total: state.total,
      loading: state.loading,
      selected: state.selected,
      editVehicle: state.modals.editVehicle,
      createVehicle: state.modals.createVehicle,
      fetch: state.fetch,
    },
    setState,
  ]);

  const isEditing = editVehicle?.show === true;
  const isCreating = createVehicle?.show === true;

  const selectedVehicle = isEditing ? vehicles.find((v: Vehicle): boolean => v.id === selected[0]) : isCreating ? { active: 0 } : undefined;

  const closeModals = (): void => {
    setState((current) => ({
      ...current,
      selected: [],
      modals: {
        ...current.modals,
        editVehicle: { show: false },
        createVehicle: { show: false },
      },
    }));
  };

  const handleModalSubmit = async (updates: any): Promise<void> => {
    try {
      if (isEditing) {
        const selectedVehiclesMap: Record<string, Vehicle> = {};
        vehicles.forEach((v: Vehicle): void => {
          if (selected.includes(v.id)) selectedVehiclesMap[v.id] = v;
        });
        await handleUpdateVehicleBulk(updates, selectedVehiclesMap);
      } else {
        await handleCreateVehicleBulk(updates);
      }
      closeModals();
      await fetch();
    } catch (err) {
      handleError(err, { notification: { title: isEditing ? 'Edit Vehicle' : 'Create Vehicle' } });
    }
  };

  // Hide modals on unmount
  useEffect(() => {
    return () => {
      setState((current) => ({
        ...current,
        modals: {
          ...current.modals,
          editVehicle: { show: false },
          createVehicle: { show: false },
        },
      }));
    };
    /* ONLY ON UNMOUNT
      Ignore other dependencies so this only fires on unmount.
    */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="Vehicles page-container">
      <PageInfo>
        {vehicles.length} / {total} Vehicles
      </PageInfo>
      <LoadingBlur loading={loading} />
      <VehicleFilters />
      <VehicleTable />
      <EditVehiclesModal
        show={isEditing || isCreating}
        data={isEditing ? selectedVehicle : isCreating ? { active: 0 } : undefined}
        title={`${isEditing ? 'Edit' : 'Create'} Vehicle`}
        onHide={closeModals}
        onSubmit={handleModalSubmit}
        options={{
          fields: isEditing ? getBulkValues(vehicles.filter((v: Vehicle): boolean => selected.includes(v.id))) : undefined,
        }}
        loading={loading}
        name="editVehicle"
      />
    </div>
  );
};

export default Vehicles;
